/*
 * Placeholder handling for the admin Prompt Editor (090).
 *
 * Splits a prompt into text and placeholder segments so both can be rendered differently -- the
 * editor highlights the placeholders, and the preview swaps in whatever the admin typed.
 *
 * Only names the prompt actually declares are treated as placeholders. That matters: some prompts
 * contain literal braces (the metrics parser embeds a JSON skeleton), and a regex that grabbed
 * every {word} would highlight those as variables and mangle them on copy.
 */

const escapeForRegExp = (name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/*
 * Returns [{type: 'text', value}, {type: 'variable', name, raw}] in document order. Both brace
 * styles are matched, matching substitutePlaceholders() on the API: the newer prompts use {{x}},
 * the config prompts written earlier use {x}.
 */
const splitPlaceholders = (text, variableNames = []) => {
	if (!text) return []
	if (!variableNames.length) return [{type: 'text', value: text}]

	const names = variableNames.map(escapeForRegExp).join('|')
	const pattern = new RegExp(`\\{\\{\\s*(${names})\\s*\\}\\}|\\{\\s*(${names})\\s*\\}`, 'g')

	const segments = []
	let lastIndex = 0
	let match

	while ((match = pattern.exec(text)) !== null) {
		if (match.index > lastIndex) {
			segments.push({type: 'text', value: text.slice(lastIndex, match.index)})
		}
		segments.push({type: 'variable', name: match[1] || match[2], raw: match[0]})
		lastIndex = match.index + match[0].length
	}

	if (lastIndex < text.length) {
		segments.push({type: 'text', value: text.slice(lastIndex)})
	}
	return segments
}

/*
 * The prompt with values substituted, for the copy button. A variable left blank keeps its
 * placeholder rather than collapsing to an empty string -- pasting a prompt with a silent hole in
 * it into a sandbox would be worse than pasting one that still shows what is missing.
 */
const applyValues = (text, variableNames, values) =>
	splitPlaceholders(text, variableNames)
		.map(segment => {
			if (segment.type === 'text') return segment.value
			const value = values[segment.name]
			return value === undefined || value === '' ? segment.raw : value
		})
		.join('')

/*
 * Placeholder-shaped tokens in the text that the prompt does not declare. These are silent bugs:
 * nothing substitutes them, so the model is handed a literal `{whatever}` in the middle of an
 * instruction. One such token was already live in QUESTIONS_FOR_TOPIC_PROMPT when this was written.
 *
 * Only identifier-shaped names count, so a JSON skeleton inside a prompt is not mistaken for one.
 */
const findUnknownPlaceholders = (text, variableNames = []) => {
	if (!text) return []
	const declared = new Set(variableNames)
	const found = new Set()

	for (const match of text.matchAll(/\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi)) found.add(match[1])
	for (const match of text.matchAll(/\{\s*([a-z_][a-z0-9_]*)\s*\}/gi)) found.add(match[1])

	return [...found].filter(name => !declared.has(name))
}

const countUnfilled = (text, variableNames, values) =>
	splitPlaceholders(text, variableNames)
		.filter(s => s.type === 'variable' && !values[s.name])
		.map(s => s.name)
		.filter((name, i, all) => all.indexOf(name) === i)
		.length

export {splitPlaceholders, applyValues, findUnknownPlaceholders, countUnfilled}
